import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from 'components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Loader2Icon } from 'components/icons';
import { FilterTriggerIndicator } from 'pages/audit-trail/FilterTriggerIndicator';
import { FILTER_TRIGGER_CLASSES } from 'pages/audit-trail/toolbarStyles';
import { useState } from 'react';
import { AuditEventCategory, EntityAuditTrailAuthorDto } from 'types/entity-audit-trail';
import { t } from 'utils/getTranslations';
import { EntityAuditTrailDescriptor } from './entityAuditTrail';

/**
 * The trail's person filter: only the people who appear in this entity's trail, plus a pinned
 * "System" choice — audit rows with no human author (auto-generation, recalculation, sync).
 */
export function AuditPersonFilter({
    trail,
    entityId,
    categories,
    selected,
    onChange,
    includeSystem,
    onIncludeSystemChange,
}: {
    trail: EntityAuditTrailDescriptor;
    entityId: number;
    categories: AuditEventCategory[];
    selected: number[];
    onChange: (selected: number[]) => void;
    includeSystem: boolean;
    onIncludeSystemChange: (includeSystem: boolean) => void;
}) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const { data: authors = [], isLoading } = trail.useAuthorsQuery({ entityId, categories }, { skip: !open });

    const normalizedSearch = search.trim().toLowerCase();
    const visibleAuthors = authors.filter((author) => author.name.toLowerCase().includes(normalizedSearch));

    const toggle = (author: EntityAuditTrailAuthorDto) => {
        onChange(selected.includes(author.id) ? selected.filter((id) => id !== author.id) : [...selected, author.id]);
    };

    const appliedCount = selected.length + (includeSystem ? 1 : 0);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger className={FILTER_TRIGGER_CLASSES}>
                {t(`${trail.localeNamespace}.person`)}
                <FilterTriggerIndicator count={appliedCount} />
            </PopoverTrigger>
            <PopoverContent
                align="start"
                // Opens from the entity history drawer, which stacks above the popover's own tier.
                positionerClassName="z-(--z-dropdown)"
                className="w-[280px] rounded-xl p-1.5"
            >
                {/* The "System" item must stay pinned while typing, so the list is filtered by hand. */}
                <Command shouldFilter={false}>
                    <CommandInput placeholder={t(`${trail.localeNamespace}.person`)} onValueChange={setSearch} />
                    <CommandList className="max-h-[300px] [scrollbar-gutter:stable]">
                        <CommandGroup>
                            <CommandItem value="__system__" onSelect={() => onIncludeSystemChange(!includeSystem)}>
                                <Checkbox checked={includeSystem} tabIndex={-1} className="pointer-events-none" />
                                {t(`${trail.localeNamespace}.system`)}
                            </CommandItem>
                        </CommandGroup>
                        {isLoading && (
                            <div className="text-muted-foreground flex items-center justify-center py-3">
                                <Loader2Icon className="size-3.5 animate-spin" />
                            </div>
                        )}
                        {!isLoading && visibleAuthors.length === 0 && (
                            <CommandEmpty>{t('audit-trail.no-results')}</CommandEmpty>
                        )}
                        <CommandGroup>
                            {visibleAuthors.map((author) => (
                                <CommandItem key={author.id} value={String(author.id)} onSelect={() => toggle(author)}>
                                    <Checkbox
                                        checked={selected.includes(author.id)}
                                        tabIndex={-1}
                                        className="pointer-events-none"
                                    />
                                    {author.name}
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                    <div className="border-grey-100 border-t pt-1">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="text-grey-600 w-full text-[12.5px]"
                            disabled={appliedCount === 0}
                            onClick={() => {
                                onChange([]);
                                onIncludeSystemChange(false);
                            }}
                        >
                            {t('audit-trail.clear-all')}
                        </Button>
                    </div>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
